import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Snackbar
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { axiosCall } from "../utils/axiosCall";
import constants from "../config/constants";
import { logOut } from "../redux/features/userSlice";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!user?.resetToken) {
      setErrorMessage("Reset session expired. Please request OTP again.");
    }
  }, [user?.resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
    // password reset logic 
    try {
      if (!user?.resetToken) {
        setErrorMessage("Reset session expired. Please request OTP again.");
        return;
      }

      const resetPassword = await axiosCall(
        constants.POST_METHOD,
        constants.RESET_PASSWORD,
        { password },
        null,
        user?.resetToken
      );

      if (resetPassword.isSuccess) {
        dispatch(logOut());
        setOpen(true);
        // Navigate to the login page after 6 seconds
        setTimeout(() => {
          navigate("/");
        }, 6000);
      } else {
        setErrorMessage(resetPassword.error.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  return (
    <Box display="flex" height="100vh">
      {/* Left Image Section */}
      <Box width="50%" overflow="hidden">
        <img
          src="/Images/leftlogo.png"
          alt="Doctor holding scalpel"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>

      {/* Right Content Section */}
      <Box
        maxWidth={"50%"}
        p={5}
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        ml={5}
      >
        {/* Logo and Text */}
        <Box display="flex" alignItems="center" mb={3} mt={3}>
          <img
            src="/Images/plus.png"
            alt="Plus sign"
            style={{ width: 40, height: 40, marginRight: 12, marginTop: -20 }}
          />
          <Box ml={2} mb={-3} fontSize={12} lineHeight={1.2}>
            <Typography
              variant="h6"
              color="#00C6F2"
              fontWeight="bold"
              style={{ marginTop: -40 }}
            >
              HOSPITAL MANAGEMENT
            </Typography>
            <Typography variant="h6" color="textSecondary" fontWeight={600}>
              INFORMATION SYSTEM
            </Typography>
          </Box>
        </Box>

        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          mt={-7}
          sx={{ width: "30rem" }}
        >
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Welcome to
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color="textPrimary"
            gutterBottom
          >
            HMIS PORTAL
          </Typography>
          <Typography
            variant="h6"
            color="#00C6F2"
            fontWeight="bold"
            mb={2}
            fontFamily="Helvetica"
            gutterBottom
          >
            HOSPITAL LOGO
          </Typography>

          <p className="enter">Enter Your Code</p>
          <p className="email-sent">
            We sent a code to <span className="email">{user.email}</span>
          </p>
          <form onSubmit={handleSubmit}>
            <Box mb={2} width="100%">
              <Box
                component="label"
                htmlFor="password"
                sx={{ color: "#666", display: "block", mb: 1 }}
              >
                Enter Password
              </Box>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                id="password"
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </IconButton>
                  ),
                }}
                required
              />
            </Box>

            <Box mb={2} sx={{ width: "100%" }}>
              <Box
                component="label"
                htmlFor="password"
                sx={{ color: "#666", display: "block", mb: 1 }}
              >
                Enter Password Again
              </Box>
              <TextField
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                variant="outlined"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <EyeOff /> : <Eye />}
                    </IconButton>
                  ),
                }}
                required
                sx={{
                  marginBottom: 2, // Additional spacing if needed
                  width: "100%", // Ensures the field takes up full width
                }}
              />
            </Box>

            {errorMessage && (
              <Typography color="error" variant="body2" mb={2}>
                {errorMessage}
              </Typography>
            )}

            <Button type="submit" variant="contained" color="primary" fullWidth>
              Reset Password
            </Button>
          </form>
        </Box>
      </Box>
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        message="Password Reset Successful! Please Login."
      />
    </Box>
  );
};

export default ResetPassword;
